<template lang="pug">
.ticket-create
  TicketCreateForm(
    v-model:name="name",
    v-model:description="description",
    v-model:property-values="propertyValues",
    template-type="tm.ticket.maintenance",
    :title-order="4",
    :included-property-custom-ids="includedPropertyCustomIds",
    :suppressed-property-custom-ids="suppressedPropertyCustomIds",
    :property-labels="propertyLabels",
    :property-visibility="propertyVisibility",
    :additional-properties="additionalProperties",
    :additional-request="additionalRequest",
    :additional-missing-required-fields="additionalMissingRequiredFields",
    show-draft,
    @created="onCreated"
  )
    template(#before-title)
      SmxFormItem(:label="$t('ticketType')", required)
        el-select(v-model="ticketType")
          el-option(
            v-for="ticket in ticketTypes",
            :key="ticket",
            :label="$t(ticket)",
            :value="ticket"
          )

    template(#property-asset="{ values, disabled, updateValues }")
      TwinSelect(
        :model-value="getAssetValue(values[ticketProps.asset])",
        clearable,
        :multiple="false",
        :query="{ isTemplate: false, type: productionPrefix + '*', active: true }",
        :props-in-label="['location', 'serialNumber']",
        show-details,
        emit-ids,
        :disabled="disabled",
        @update:model-value="updateValues({ [ticketProps.asset]: $event?.[0] })",
        @twin-selected="onTwinSelect"
      )

    template(#property-startDate="{ values, disabled }")
      el-date-picker(
        :model-value="[values[ticketProps.startDate], values[ticketProps.dueDate]]",
        duration,
        type="daterange",
        :range-separator="$t('to')",
        format="DD/MM/YYYY",
        value-format="YYYY-MM-DD",
        :disabled="disabled",
        @update:model-value="updateDateRange"
      )

    template(#after-property="{ property }")
      template(v-if="property.customId === ticketProps.area")
        SmxFormItem(:label="$t('images')")
          TwinImages(v-model="images")

        SmxFormItem(
          v-if="ticketType === 'damageNotice'",
          :label="$t('causeCategory')"
        )
          ValueListSelect(v-model="causeCategory", custom-id="causeCategory")

        SmxFormItem(
          :label="ticketType === 'damageNotice' ? $t('damageDescription') : $t('description')"
        )
          el-input(v-model="description", type="textarea", :rows="10")

        SmxFormItem(v-if="isAdmin", :label="$t('sops')")
          .content
            el-button.mb-4(icon="Plus", @click="addSopVisible = true") {{ $t("addSop") }}
            el-table(
              v-if="selectedSops.length > 0",
              :data="selectedSops",
              :show-header="false"
            )
              el-table-column(prop="name")
              el-table-column(:width="50")
                template(#default="scope")
                  .pl-1.pr-1.cursor-pointer(
                    @click="removeSop(scope.row.id, scope.row.name)"
                  )
                    el-icon
                      Delete

  el-dialog(
    v-model="addSopVisible",
    :title="$t('addSop')",
    @close="closeAddSopDialog"
  )
    smx-form
      smx-form-item(:label="$t('sop')")
        SopSelect(
          v-model="preselectSop",
          :query="{ active: true }",
          type="sop"
        )
      smx-form-item(:label="$t('title')")
        el-input(v-model="sopTitle")
    template(#footer)
      el-button(type="danger", @click="closeAddSopDialog") {{ $t("cancel") }}
      el-button(type="primary", @click="addSopToList") {{ $t("add") }}

  el-dialog(v-model="createDialogVisible", :show-close="false", width="500")
    .progress-dialog
      template(v-for="item in imageUploadStates", :key="item.id")
        .justify-self-end {{ item.name }}
        el-progress(
          :percentage="item.progress",
          :status="item.loadState.loaded ? 'success' : item.loadState.error ? 'exception' : ''"
        )
    template(v-if="createHasError", #footer)
      el-button(
        type="primary",
        @click="createdTicketId ? emit('created', createdTicketId) : (createDialogVisible = false)"
      ) {{ $t("close") }}
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { isNil } from "lodash";
import SmxFormItem from "@shared/components/elements/SmxFormItem.vue";
import TicketCreateForm from "@shared/components/tickets/components/TicketCreateForm.vue";
import TwinSelect from "@shared/components/twinManagement/components/TwinSelect.vue";
import ValueListSelect from "@shared/components/twinManagement/components/ValueListSelect.vue";
import TwinImages from "@shared/components/elements/TwinImages.vue";
import { type TwinResponse } from "@shared/services/twinManagement";
import { type SOP } from "../service";
import SopSelect from "./SopSelect.vue";
import { useMaintenanceStore } from "../store";
import {
  clientId,
  maintenanceTicketProps as ticketProps,
  roles,
} from "../constants";
import { prefix as productionPrefix } from "@shared/assetManagement/constants";
import { useAuthStore } from "@shared/stores/auth";
import { useI18n } from "vue-i18n";
import { postDocument } from "@shared/services/documents";
import {
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";

const props = defineProps<{
  assetId?: string;
}>();

const emit = defineEmits<{
  created: [ticketId: string];
}>();

const { t } = useI18n();
const route = useRoute();
const authStore = useAuthStore();
const maintenanceStore = useMaintenanceStore();
const name = ref("");
const description = ref("");
const ticketType = ref("damageNotice");
const causeCategory = ref<string | null>(null);
const propertyValues = ref<Record<string, unknown>>({
  [ticketProps.asset]: props.assetId,
  [ticketProps.maintenanceRequestType]: ticketType.value,
  [ticketProps.createdBy]: authStore.userId ? [authStore.userId] : [],
});

const isAdmin = computed(() =>
  authStore.rolesByClientId[clientId]?.roles.includes(roles.admin),
);
const isPlanner = computed(() =>
  authStore.rolesByClientId[clientId]?.roles.includes(roles.planner),
);
const ticketTypes = ["damageNotice", "maintenance", "inspection"];
const includedPropertyCustomIds = [
  ticketProps.asset,
  ticketProps.area,
  ticketProps.requester,
  ticketProps.assignee,
  ticketProps.contributors,
  ticketProps.startDate,
  ticketProps.dueDate,
  ticketProps.priority,
  ticketProps.ticketNumber,
  ticketProps.responsibility,
];
const suppressedPropertyCustomIds = [
  ticketProps.maintenanceRequestType,
  ticketProps.dueDate,
  ticketProps.causeCategory,
];
const propertyLabels = computed(() => ({
  [ticketProps.startDate]: t("plannedDuration"),
}));
const propertyVisibility = computed(() => ({
  [ticketProps.asset]: !props.assetId,
  [ticketProps.assignee]: Boolean(isAdmin.value),
  [ticketProps.contributors]: Boolean(isAdmin.value),
  [ticketProps.startDate]: Boolean(isAdmin.value || isPlanner.value),
  [ticketProps.dueDate]: Boolean(isAdmin.value || isPlanner.value),
}));
const additionalProperties = computed(() => ({
  [ticketProps.causeCategory]: causeCategory.value,
}));
const additionalRequest = computed(() => ({
  sops: selectedSops.value.map((item) => ({
    title: item.name,
    uuid: item.id,
  })),
}));

watch(ticketType, (value) => {
  propertyValues.value = {
    ...propertyValues.value,
    [ticketProps.maintenanceRequestType]: value,
  };
});
watch(causeCategory, (value) => {
  propertyValues.value = {
    ...propertyValues.value,
    [ticketProps.causeCategory]: value,
  };
});
watch(name, (newValue, oldValue) => {
  if (oldValue === description.value) description.value = newValue;
});

function additionalMissingRequiredFields() {
  const assetId = propertyValues.value[ticketProps.asset];
  const location = propertyValues.value[ticketProps.area];
  return !props.assetId &&
    isNil(assetId) &&
    (typeof location !== "string" || location.trim() === "")
    ? [t("assetOrLocation")]
    : [];
}

function updateDateRange(value: string[] | null) {
  propertyValues.value = {
    ...propertyValues.value,
    [ticketProps.startDate]: value?.[0],
    [ticketProps.dueDate]: value?.[1],
  };
}

function getAssetValue(value: unknown) {
  if (value === null || value === undefined || Array.isArray(value)) {
    return value;
  }
  return [value];
}

function onTwinSelect(twin: TwinResponse) {
  const areaValue = twin.properties?.find(
    (property) => property.customId === ticketProps.area,
  )?.propertyValue?.value;
  propertyValues.value = {
    ...propertyValues.value,
    [ticketProps.asset]: twin.uuid,
    [ticketProps.area]: typeof areaValue === "string" ? areaValue : "",
  };
}

interface ImageWithFile {
  id: string;
  file: File;
  purpose?: string;
}
interface ImageUploadState {
  id: string;
  name: string;
  loadState: typeof stateInitial;
  progress: number;
}
const images = ref<ImageWithFile[]>([]);
const imageUploadStates = ref<ImageUploadState[]>([]);
const createDialogVisible = ref(false);
const createdTicketId = ref<string | null>(null);
const createHasError = computed(() =>
  imageUploadStates.value.some((item) => item.loadState.error),
);

async function onCreated(ticketId: string) {
  createdTicketId.value = ticketId;
  if (images.value.length === 0) {
    emit("created", ticketId);
    return;
  }

  imageUploadStates.value = images.value.map((image) => ({
    id: image.id,
    name: image.file.name,
    loadState: { ...stateLoading },
    progress: 0,
  }));
  createDialogVisible.value = true;

  await Promise.all(images.value.map((image) => uploadImage(image, ticketId)));
  if (!createHasError.value) emit("created", ticketId);
}

async function uploadImage(image: ImageWithFile, ticketId: string) {
  const formData = new FormData();
  formData.append("name", image.file.name);
  formData.append("category", "twinImage");
  formData.append("type", "image");
  formData.append("entityType", "twin");
  formData.append("entityId", ticketId);
  if (image.purpose) formData.append("purpose", image.purpose);
  formData.append("file", image.file, image.file.name);

  await postDocument(formData, (progress) => {
    const uploadState = imageUploadStates.value.find(
      (item) => item.id === image.id,
    );
    if (uploadState && progress.total) {
      uploadState.progress = Number(
        ((progress.loaded * 100) / progress.total).toFixed(0),
      );
    }
  }).then(
    () => setImageUploadState(image.id, stateLoaded),
    () => setImageUploadState(image.id, stateError),
  );
}

function setImageUploadState(id: string, loadState: typeof stateInitial) {
  const uploadState = imageUploadStates.value.find((item) => item.id === id);
  if (uploadState) uploadState.loadState = { ...loadState };
}

const addSopVisible = ref(false);
const sopTitle = ref("");
const preselectSop = ref<SOP | null>(null);
const selectedSops = ref<{ id: string; name: string; sop: SOP }[]>([]);

watch(preselectSop, () => {
  if (preselectSop.value) sopTitle.value = preselectSop.value.name;
});

function addSopToList() {
  if (preselectSop.value) {
    selectedSops.value.push({
      id: preselectSop.value.uuid,
      name: sopTitle.value,
      sop: preselectSop.value,
    });
  }
  closeAddSopDialog();
}

function closeAddSopDialog() {
  addSopVisible.value = false;
  preselectSop.value = null;
  sopTitle.value = "";
}

function removeSop(id: string, name: string) {
  const index = selectedSops.value.findIndex(
    (item) => item.id === id && item.name === name,
  );
  if (index !== -1) selectedSops.value.splice(index, 1);
}

onMounted(() => {
  void maintenanceStore.loadSops({});
  if (route.query.date) {
    updateDateRange([route.query.date as string, route.query.date as string]);
  }
});
</script>

<style scoped>
.progress-dialog {
  display: grid;
  grid-template-columns: minmax(min-content, 1fr) 1fr;
  align-items: center;
  gap: 1rem;
}
</style>
